import { countyLabel } from "@nwga/contracts/region";
import { ReviewQueue, WaitingFor } from "@nwga/features/admin/ui";
import type { BusinessStatus } from "@nwga/features/businesses/domain";
import { BusinessStatusBadge } from "@nwga/features/businesses/ui";
import { useTRPC } from "@nwga/features/client";
import { Button } from "@nwga/ui/components/button";
import { EmptyState } from "@nwga/ui/components/empty-state";
import { Input } from "@nwga/ui/components/input";
import { Skeleton } from "@nwga/ui/components/skeleton";
import {
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
} from "@nwga/ui/components/tabs";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { BuildingIcon } from "lucide-react";
import * as React from "react";
import { z } from "zod";

/**
 * Businesses, grouped by review state.
 *
 * "Waiting approval" is its own tab rather than a filter on a list because
 * reviewing and browsing are different tasks: the queue is worked top to
 * bottom with the decisions inline, while the other tabs are looked *through*
 * for one business. "Active" is the default because it's the state almost
 * every business is in, and the one a reviewer looks up most often — the
 * queue already has its own badge on the admin nav for when it needs
 * attention.
 */

const TABS = ["active", "pending", "suspended", "rejected", "all"] as const;
type Tab = (typeof TABS)[number];

const searchSchema = z.object({
	tab: z.enum(TABS).default("active"),
});

export const Route = createFileRoute("/admin/businesses/")({
	component: AdminBusinessesPage,
	validateSearch: searchSchema,
	loader: ({ context }) =>
		Promise.all([
			context.queryClient.ensureQueryData(
				context.trpc.admin.reviewQueue.queryOptions(),
			),
			context.queryClient.ensureQueryData(
				context.trpc.admin.stats.queryOptions(),
			),
		]),
});

function AdminBusinessesPage() {
	const { tab } = Route.useSearch();
	const navigate = Route.useNavigate();
	const trpc = useTRPC();
	const stats = useQuery(trpc.admin.stats.queryOptions());

	return (
		<div className="space-y-6">
			<div>
				<h1 className="font-semibold text-2xl tracking-tight">Businesses</h1>
				<p className="mt-1 text-muted-foreground text-sm">
					Review registrations, and find any business on the platform.
				</p>
			</div>

			<Tabs
				value={tab}
				// The tab lives in the URL so a reviewer can bookmark a view (the
				// queue, say) and the back button behaves.
				onValueChange={(value) =>
					void navigate({ search: { tab: value as Tab } })
				}
			>
				<TabsList>
					<TabsTrigger value="active">Active</TabsTrigger>
					<TabsTrigger value="pending">
						Waiting approval
						{stats.data === undefined ? null : ` (${stats.data.pendingReview})`}
					</TabsTrigger>
					<TabsTrigger value="suspended">
						Suspended
						{stats.data === undefined
							? null
							: ` (${stats.data.suspendedBusinesses})`}
					</TabsTrigger>
					<TabsTrigger value="rejected">Changes requested</TabsTrigger>
					<TabsTrigger value="all">All</TabsTrigger>
				</TabsList>

				<TabsContent value="active" className="pt-4">
					<BusinessList
						status="active"
						emptyDescription="No approved businesses yet."
					/>
				</TabsContent>

				<TabsContent value="pending" className="pt-4">
					<ReviewQueue />
				</TabsContent>

				<TabsContent value="suspended" className="pt-4">
					<BusinessList
						status="suspended"
						emptyDescription="No suspended businesses."
					/>
				</TabsContent>

				<TabsContent value="rejected" className="pt-4">
					<BusinessList
						status="rejected"
						emptyDescription="No businesses waiting on changes."
					/>
				</TabsContent>

				<TabsContent value="all" className="pt-4">
					<BusinessList emptyDescription="Nobody has registered a business yet." />
				</TabsContent>
			</Tabs>
		</div>
	);
}

function BusinessList({
	status,
	emptyDescription,
}: {
	readonly status?: BusinessStatus | undefined;
	readonly emptyDescription: string;
}) {
	const trpc = useTRPC();
	const [search, setSearch] = React.useState("");
	const [debounced, setDebounced] = React.useState("");

	React.useEffect(() => {
		const timer = setTimeout(() => setDebounced(search.trim()), 250);
		return () => clearTimeout(timer);
	}, [search]);

	const query = useInfiniteQuery(
		trpc.businesses.list.infiniteQueryOptions(
			{
				limit: 25,
				...(status === undefined ? {} : { status }),
				...(debounced.length === 0 ? {} : { search: debounced }),
			},
			{ getNextPageParam: (page) => page.nextCursor },
		),
	);

	const businesses = query.data?.pages.flatMap((page) => page.items) ?? [];

	return (
		<div className="space-y-4">
			<Input
				value={search}
				onChange={(event) => setSearch(event.target.value)}
				placeholder="Search by name or city"
				aria-label="Search businesses"
				className="max-w-sm"
			/>

			{query.isPending ? (
				<Skeleton className="h-64 w-full" />
			) : businesses.length === 0 ? (
				<EmptyState
					icon={BuildingIcon}
					title="No businesses found"
					description={
						debounced.length === 0
							? emptyDescription
							: `Nothing matched "${debounced}".`
					}
				/>
			) : (
				<ul className="space-y-2">
					{businesses.map((business) => (
						<li key={business.id}>
							<Link
								to="/admin/businesses/$businessId"
								params={{ businessId: business.id }}
								className="flex flex-wrap items-center justify-between gap-3 rounded-lg border bg-card p-4 transition-colors hover:border-primary/40 hover:bg-accent/30"
							>
								<div className="min-w-0">
									<p className="truncate font-medium">{business.name}</p>
									<p className="text-muted-foreground text-sm">
										{countyLabel(business.county)}
										{business.city === null ? null : ` · ${business.city}`}
										{business.status === "pending" && (
											<>
												{" · "}
												<WaitingFor since={business.submittedAt} />
											</>
										)}
									</p>
								</div>
								<BusinessStatusBadge status={business.status} />
							</Link>
						</li>
					))}
				</ul>
			)}

			{query.hasNextPage && (
				<Button
					variant="outline"
					disabled={query.isFetchingNextPage}
					onClick={() => void query.fetchNextPage()}
				>
					{query.isFetchingNextPage ? "Loading…" : "Load more"}
				</Button>
			)}
		</div>
	);
}
